/**
 * Campaign name validation utilities for Google Ads API compliance.
 *
 * Google Ads API Requirements:
 * - Campaign names must be between 1 and 128 Unicode characters
 * - Names cannot contain control characters (newlines, tabs, etc.)
 * - Names cannot be empty after trimming whitespace
 */

export interface CampaignNameValidationResult {
  isValid: boolean;
  error: string | null;
  sanitizedName: string;
}

/**
 * Validates a campaign name against Google Ads API requirements.
 *
 * @param name - The campaign name to validate
 * @returns Validation result with error message if invalid
 */
export function validateCampaignName(name: string): CampaignNameValidationResult {
  const trimmedName = name.trim();

  // Check if empty
  if (trimmedName.length === 0) {
    return {
      isValid: false,
      error: 'Campaign name cannot be empty',
      sanitizedName: trimmedName,
    };
  }

  // Check length (1-128 Unicode characters)
  if (trimmedName.length > 128) {
    return {
      isValid: false,
      error: 'Campaign name must be 128 characters or less',
      sanitizedName: trimmedName,
    };
  }

  // Check for control characters (ASCII 0-31 and 127)
  // These include newlines (\n), tabs (\t), carriage returns (\r), etc.
  // eslint-disable-next-line no-control-regex
  const hasControlCharacters = /[\x00-\x1F\x7F]/.test(trimmedName);
  if (hasControlCharacters) {
    return {
      isValid: false,
      error: 'Campaign name cannot contain control characters (newlines, tabs, etc.)',
      sanitizedName: trimmedName,
    };
  }

  return {
    isValid: true,
    error: null,
    sanitizedName: trimmedName,
  };
}

/**
 * Sanitizes a campaign name by removing control characters and truncating to max length.
 * Useful for auto-correcting user input.
 *
 * @param name - The campaign name to sanitize
 * @returns Sanitized name that meets Google Ads requirements
 */
export function sanitizeCampaignName(name: string): string {
  // Remove control characters
  // eslint-disable-next-line no-control-regex
  let sanitized = name.replace(/[\x00-\x1F\x7F]/g, '');

  // Trim whitespace
  sanitized = sanitized.trim();

  // Truncate to 128 characters
  if (sanitized.length > 128) {
    sanitized = sanitized.substring(0, 128);
  }

  return sanitized;
}

/**
 * Gets a user-friendly character count message for campaign names.
 *
 * @param name - The campaign name
 * @returns Character count message (e.g., "50/128 characters")
 */
export function getCampaignNameCharacterCount(name: string): string {
  const trimmedLength = name.trim().length;
  return `${trimmedLength}/128 characters`;
}

/**
 * Checks if a campaign name is approaching the character limit.
 *
 * @param name - The campaign name
 * @param threshold - Character count threshold (default: 120)
 * @returns True if name length is at or above threshold
 */
export function isCampaignNameNearLimit(name: string, threshold: number = 120): boolean {
  return name.trim().length >= threshold;
}
